import { useState, useRef, useCallback } from 'react';
import { Crosshair, Save, Download, Trash2, Undo2 } from 'lucide-react';
import { toast } from 'sonner';

const ZONE_BOUNDARY = 40.0;

export const PositionCaptureMode = ({ mapImage, onSavePositions, existingPositions = [] }) => {
  const [capturedPositions, setCapturedPositions] = useState([]);
  const [nextUnitNumber, setNextUnitNumber] = useState('');
  const [isCapturing, setIsCapturing] = useState(false);
  const [villaType, setVillaType] = useState('B');
  const mapRef = useRef(null);
  const imageRef = useRef(null);

  const existingNumbers = new Set(existingPositions.map(p => `${p.villa_type || 'B'}-${p.unit_number || p.villa_number}`));
  const capturedNumbers = new Set(capturedPositions.map(p => `${p.villa_type}-${p.unit_number}`));

  const handleMapClick = useCallback((e) => {
    if (!isCapturing || !nextUnitNumber) return;

    const num = parseInt(nextUnitNumber);
    if (isNaN(num) || num <= 0) {
      toast.error('Enter a valid unit number');
      return;
    }

    const key = `${villaType}-${num}`;
    if (existingNumbers.has(key)) {
      toast.error(`Unit ${villaType}-${num} already exists in database`);
      return;
    }
    if (capturedNumbers.has(key)) {
      toast.error(`Unit ${villaType}-${num} already captured`);
      return;
    }

    // Calculate position as percentage of image
    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    // Only allow Zone 1 (below road, y >= 40%)
    if (y < ZONE_BOUNDARY) {
      toast.error('Only Zone 1 (below the road) is active. Click below the road.');
      return;
    }

    const position = {
      unit_number: num,
      villa_type: villaType,
      x: Math.round(x * 100) / 100,
      y: Math.round(y * 100) / 100,
      zone: 'zone_1',
    };

    setCapturedPositions(prev => [...prev, position]);
    toast.success(`Captured Unit ${villaType}-${num} at (${position.x.toFixed(1)}%, ${position.y.toFixed(1)}%)`);

    // Auto-increment
    setNextUnitNumber(String(num + 1));
  }, [isCapturing, nextUnitNumber, villaType, existingNumbers, capturedNumbers]);

  const handleUndo = () => {
    if (capturedPositions.length === 0) return;
    const last = capturedPositions[capturedPositions.length - 1];
    setCapturedPositions(prev => prev.slice(0, -1));
    setNextUnitNumber(String(last.unit_number));
    toast.info(`Removed Unit ${last.villa_type}-${last.unit_number}`);
  };

  const handleClear = () => {
    if (capturedPositions.length === 0) return;
    if (window.confirm(`Clear all ${capturedPositions.length} captured positions?`)) {
      setCapturedPositions([]);
      toast.info('All positions cleared');
    }
  };

  const handleSave = () => {
    if (capturedPositions.length === 0) {
      toast.error('No positions to save');
      return;
    }
    onSavePositions(capturedPositions);
  };

  const handleExportJSON = () => {
    if (capturedPositions.length === 0) {
      toast.error('No positions to export');
      return;
    }
    const data = JSON.stringify(capturedPositions, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `positions_zone1_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${capturedPositions.length} positions`);
  };

  return (
    <div className="space-y-4" data-testid="position-capture-mode">
      {/* Controls */}
      <div className="bg-white/5 border border-white/10 p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Crosshair className="w-5 h-5 text-[#D4AF37]" />
            <h3 className="text-white font-medium">Position Capture Mode</h3>
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-green-500/20 text-green-400 border border-green-500/30">
              Zone 1 Only
            </span>
          </div>
          <div className="text-sm text-white/40">
            {capturedPositions.length} captured
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-4">
          {/* Villa Type */}
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-wider text-white/40">Type</label>
            <select
              value={villaType}
              onChange={(e) => setVillaType(e.target.value)}
              className="bg-black border border-white/10 text-white text-sm px-3 py-2 focus:outline-none focus:border-[#D4AF37]"
              data-testid="capture-villa-type"
            >
              <option value="A">Type A (300m²)</option>
              <option value="B">Type B (203m²)</option>
              <option value="C">Type C (160m²)</option>
            </select>
          </div>

          {/* Unit Number */}
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-wider text-white/40">Next Unit #</label>
            <input
              type="number"
              value={nextUnitNumber}
              onChange={(e) => setNextUnitNumber(e.target.value)}
              placeholder="1"
              className="w-24 bg-black border border-white/10 text-white text-sm px-3 py-2 focus:outline-none focus:border-[#D4AF37]"
              data-testid="capture-unit-number"
            />
          </div>

          {/* Toggle */}
          <button
            onClick={() => setIsCapturing(!isCapturing)}
            className={`px-5 py-2 text-xs uppercase tracking-wider font-medium transition-all ${
              isCapturing
                ? 'bg-red-500 text-white hover:bg-red-600'
                : 'bg-[#D4AF37] text-black hover:bg-[#C5A059]'
            }`}
            data-testid="capture-toggle-btn"
          >
            {isCapturing ? 'Stop Capturing' : 'Start Capturing'}
          </button>

          {/* Undo */}
          <button
            onClick={handleUndo}
            disabled={capturedPositions.length === 0}
            className="p-2 border border-white/10 text-white/60 hover:text-white hover:border-white/30 transition-colors disabled:opacity-30"
            title="Undo last"
            data-testid="capture-undo-btn"
          >
            <Undo2 className="w-4 h-4" />
          </button>

          {/* Clear */}
          <button
            onClick={handleClear}
            disabled={capturedPositions.length === 0}
            className="p-2 border border-white/10 text-red-400/60 hover:text-red-400 hover:border-red-400/30 transition-colors disabled:opacity-30"
            title="Clear all"
            data-testid="capture-clear-btn"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3 pt-2 border-t border-white/5">
          <button
            onClick={handleSave}
            disabled={capturedPositions.length === 0}
            className="px-5 py-2.5 bg-white text-black text-xs uppercase tracking-wider font-medium hover:bg-white/90 transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-2"
            data-testid="capture-save-btn"
          >
            <Save className="w-4 h-4" />
            Save Positions ({capturedPositions.length})
          </button>
          <button
            onClick={handleExportJSON}
            disabled={capturedPositions.length === 0}
            className="px-5 py-2.5 border border-white/20 text-white text-xs uppercase tracking-wider font-medium hover:bg-white/5 transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-2"
            data-testid="capture-export-btn"
          >
            <Download className="w-4 h-4" />
            Export JSON
          </button>
        </div>
      </div>

      {/* Map with capture overlay */}
      <div className="relative border border-white/10 bg-black" ref={mapRef}>
        {/* Zone 2 overlay (dimmed) */}
        <div
          className="absolute top-0 left-0 right-0 bg-black/60 z-10 pointer-events-none flex items-center justify-center"
          style={{ height: `${ZONE_BOUNDARY}%` }}
        >
          <span className="text-white/30 text-xs uppercase tracking-wider">Zone 2 (Inactive)</span>
        </div>

        {/* Capture instruction */}
        {isCapturing && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2 z-20 bg-[#D4AF37] text-black px-4 py-1.5 text-xs font-medium uppercase tracking-wider">
            Click on Unit #{nextUnitNumber} below the road
          </div>
        )}

        {/* Map Image */}
        <img
          ref={imageRef}
          src={mapImage}
          alt="Site Map"
          className={`w-full h-auto block select-none ${isCapturing ? 'cursor-crosshair' : ''}`}
          draggable={false}
          onClick={handleMapClick}
          data-testid="capture-map-image"
        />

        {/* Existing position markers (from DB) */}
        {existingPositions.map((pos) => (
          <div
            key={`existing-${pos.villa_type || 'B'}-${pos.unit_number || pos.villa_number}`}
            className="absolute w-2 h-2 rounded-full bg-white/20 pointer-events-none"
            style={{
              left: `${pos.position_x || pos.x}%`,
              top: `${pos.position_y || pos.y}%`,
              transform: 'translate(-50%, -50%)',
            }}
          />
        ))}

        {/* Captured position markers (highlighted) */}
        {capturedPositions.map((pos, idx) => (
          <div
            key={`captured-${pos.villa_type}-${pos.unit_number}`}
            className="absolute z-10 pointer-events-none"
            style={{
              left: `${pos.x}%`,
              top: `${pos.y}%`,
              transform: 'translate(-50%, -50%)',
            }}
          >
            <div className="relative">
              <div className="w-3 h-3 rounded-full bg-[#D4AF37] shadow-[0_0_8px_rgba(212,175,55,0.6)] animate-pulse" />
              <span className="absolute -top-4 left-1/2 -translate-x-1/2 text-[9px] text-[#D4AF37] font-bold whitespace-nowrap">
                {pos.unit_number}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Captured positions list */}
      {capturedPositions.length > 0 && (
        <div className="bg-white/5 border border-white/10 p-4 max-h-48 overflow-y-auto">
          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">
            Captured ({capturedPositions.length})
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-2">
            {capturedPositions.map((pos) => (
              <div
                key={`list-${pos.villa_type}-${pos.unit_number}`}
                className="flex items-center gap-2 px-2 py-1 bg-[#D4AF37]/10 border border-[#D4AF37]/20 text-xs"
              >
                <div className="w-2 h-2 rounded-full bg-[#D4AF37]" />
                <span className="text-white font-mono">{pos.villa_type}{pos.unit_number}</span>
                <span className="text-white/30 text-[9px]">({pos.x.toFixed(0)},{pos.y.toFixed(0)})</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
